import type { Issue, IssueStatus } from './types';
import { SIZE_POINTS } from './types';
import { addDays, daysBetween, today, type Sprint } from './sprints';
import { workingDaysInclusive, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';

/**
 * HOW MUCH WORK IS LEFT, ON EACH DAY OF A SPRINT.
 *
 * Stage 3 of *Working in sprints* in ROADMAP.md. Stages 1 and 2 made a sprint
 * exist and made it plannable; this is the part that answers the only question
 * a team asks halfway through one — are we going to finish?
 *
 * WHY IT IS RECONSTRUCTED RATHER THAN STORED. There is no nightly snapshot and
 * this deliberately does not wait for one. Migration 0018 recorded the from and
 * the to of every status change as FIELDS beside the sentence, precisely so a
 * chart could be drawn over history that already happened rather than only over
 * history collected after somebody remembered to start collecting it. That
 * decision is what makes this file possible on the day it is written, on a board
 * whose sprints started two days ago.
 *
 * BACKWARDS FROM NOW, NOT FORWARDS FROM CREATION, and this is the one thing
 * here that would be wrong if done the obvious way. Where a card stands TODAY is
 * a fact — it is in the store. Where it stood on the 3rd is that fact with three
 * days of changes taken back off it. Replaying forwards instead needs to know
 * what each card looked like when it was made, which nothing records: a card
 * created in the backlog and a card created straight into the sprint are
 * indistinguishable afterwards, and guessing wrong puts work on the chart that
 * was never in the fortnight.
 *
 * WHAT THIS CANNOT SEE, said here rather than discovered later:
 *
 *   - A card DELETED mid-sprint is gone from the store, and no history of it
 *     remains to replay. It vanishes from every earlier day too, so a sprint
 *     that deleted work looks like a sprint that never had it. Deleting is rare
 *     and it is the honest failure — the alternative is a chart plotting cards
 *     nobody can click on.
 *   - SIZE AS IT WAS ON THE DAY. Every day is drawn with each card's size as it
 *     is NOW, so re-sizing a card retroactively changes yesterday's line. The
 *     history to do better starts accumulating today — see migration 0020 —
 *     which is exactly the trade 0018 made for statuses, one stage earlier.
 */

/** Work that is finished, however it finished. */
const RESOLVED: IssueStatus[] = ['done', 'wontdo'];
const isResolved = (s: IssueStatus) => RESOLVED.indexOf(s) !== -1;

/**
 * WHAT ONE CARD IS WORTH, or nothing at all.
 *
 * `undefined` rather than 0 for an unsized card, and the distinction carries the
 * whole honesty of this file. Zero is a claim — it says this card is no work —
 * and a sprint of unsized cards would burn down to a triumphant zero with every
 * one of them still open. Undefined is the truth: nobody has said. It is counted
 * separately and reported beside the chart, where somebody can act on it.
 */
export function pointsOf(issue: Pick<Issue, 'size'>): number | undefined {
  const size = issue.size;
  return size ? SIZE_POINTS[size] : undefined;
}

/** What a set of cards adds up to, and how many of them said nothing. */
export function totalPoints(issues: Pick<Issue, 'size'>[]): { points: number; unsized: number } {
  let points = 0;
  let unsized = 0;
  for (const i of issues) {
    const p = pointsOf(i);
    if (p === undefined) unsized++;
    else points += p;
  }
  return { points, unsized };
}

/* ── Where things stood on a given day ─────────────────────────────────────── */

interface Moment {
  status: IssueStatus;
  sprint: string;
}

/** One change, flattened out of the issues and ready to be taken back off. */
interface Undo {
  at: number;
  id: string;
  /** What the field said BEFORE this change — where undoing lands. */
  status?: IssueStatus;
  sprint?: string;
}

/**
 * Every status and sprint move on the board, newest first.
 *
 * Both kinds in one list on purpose. They have to be undone in the order they
 * happened regardless of which kind they are: a card moved to done and then
 * pulled out of the sprint, taken back in the wrong order, ends up out of the
 * sprint and still done on a day when it was neither.
 */
function undoableMoves(issues: Issue[]): Undo[] {
  const out: Undo[] = [];
  for (const i of issues) {
    for (const a of i.activity ?? []) {
      const isStatus = a.toStatus !== undefined && a.toStatus !== null;
      /*
       * A SPRINT MOVE IS RECOGNISED BY `toSprint` BEING PRESENT, INCLUDING WHEN
       * IT IS EMPTY. Empty means the backlog, which is a real destination —
       * taking a card OUT of a sprint is the move most worth seeing on a chart,
       * because it is scope leaving. Testing for truthiness instead would treat
       * exactly that move as no move at all.
       */
      const isSprint = a.toSprint !== undefined && a.toSprint !== null;
      if (!isStatus && !isSprint) continue;
      out.push({
        at: a.at,
        id: i.id,
        status: isStatus ? (a.fromStatus as IssueStatus | undefined) : undefined,
        sprint: isSprint ? (a.fromSprint ?? '') : undefined,
      });
    }
  }
  return out.sort((x, y) => y.at - x.at);
}

/**
 * A day's last moment, as a number.
 *
 * The end of the day rather than the start, because "how much was left on
 * Tuesday" means at the end of Tuesday — work finished on Tuesday afternoon
 * should be off Tuesday's figure, not still on it and dropping on Wednesday.
 * Built through `Date` so the boundary is the reader's own midnight; see the
 * note about dates as strings at the top of sprints.ts.
 */
function endOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

/** The instant a day begins. Used for one figure only — see `burndown`. */
function startOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

export interface Day {
  date: string;
  /** Points still unfinished in the sprint at the end of this day. */
  remaining: number;
  /** Cards still unfinished, sized or not — the sanity check beside the points. */
  cards: number;
  /** Unfinished cards nobody has sized, which are in `cards` and not in `remaining`. */
  unsized: number;
  /**
   * Points FINISHED in this sprint by the end of this day.
   *
   * Not `committed - remaining`, and the difference is the whole reason it is
   * counted rather than subtracted. Work added mid-fortnight raises what is
   * left, so subtraction reports a team that took on extra work as having
   * finished LESS than nothing. Counted directly it says what was actually
   * completed, which is the number velocity is built from.
   */
  finished: number;
  /** The straight line from the committed total down to zero on the last day. */
  ideal: number;
  /** False for days that have not happened yet, which the chart stops drawing at. */
  actual: boolean;
}

/**
 * THE LINE. One entry per day of the sprint, first to last.
 *
 * Every day is drawn, including the ones still to come — but they are marked
 * `actual: false` and the chart stops the real line there. A burndown drawn only
 * up to today has no room left on the right, so a sprint three days in looks
 * finished; drawn to the end with the line stopping where the facts stop, the
 * gap between the two is the entire message.
 */
export function burndown(
  issues: Issue[],
  sprint: Pick<Sprint, 'id' | 'startsOn' | 'endsOn' | 'startedAt' | 'closedAt'>,
  now: number = Date.now(),
  cal: WorkCalendar = DEFAULT_CALENDAR,
): Day[] {
  /*
   * IT OPENS ON THE DAY IT ACTUALLY STARTED, not the day it was planned to.
   * A sprint planned for Monday and started on Wednesday is two days shorter,
   * and drawing from `startsOn` opens the chart with two days of imaginary
   * flatness before anybody had begun — which reads as a slow start rather than
   * as a late one. `startedAt` is stored for this and nothing else.
   */
  const from = sprint.startedAt ? isoOf(sprint.startedAt) : sprint.startsOn;
  const first = from < sprint.endsOn ? from : sprint.endsOn;
  /*
   * `daysBetween` COUNTS BOTH ENDS — the 1st to the 5th is five days, not four.
   * Written as `n <= span` this drew a sixth day past the end of every sprint,
   * which then became the last day: the ideal line reached zero a day late, and
   * every velocity figure was measured on a day the sprint did not have.
   */
  const span = daysBetween(first, sprint.endsOn);
  const dates: string[] = [];
  for (let n = 0; n < span; n++) dates.push(addDays(first, n));

  const state = new Map<string, Moment>();
  for (const i of issues) state.set(i.id, { status: i.status, sprint: i.sprint ?? '' });
  const moves = undoableMoves(issues);
  const points = new Map(issues.map((i) => [i.id, pointsOf(i)]));
  const born = new Map(issues.map((i) => [i.id, i.createdAt]));

  /*
   * TODAY, OR THE DAY IT CLOSED. A finished sprint's chart must not keep
   * redrawing its last day as more work happens elsewhere on the board — the
   * fortnight is over and its shape is settled. A running one is drawn up to
   * today and no further.
   */
  const stop = sprint.closedAt ? isoOf(sprint.closedAt) : today(now);

  /*
   * WALKED BACKWARDS, which is why the answers come out in reverse and get
   * turned round at the end. Each step takes off every change made after that
   * day's last moment, so the state left behind is the state as it stood then.
   */
  /*
   * ── ONE MORE POINT THAN THERE ARE DAYS ──────────────────────────────────
   *
   * Mark 0 is the sprint's OPENING; every mark after it is a day's close.
   *
   * It was one point per day, each read at the day's END — so anything finished
   * on day one was already burned off before the chart's first point, and the
   * line began at what was LEFT. FlightDeck's own Sprint 1 committed 48 points,
   * finished 12 on its opening day, and drew a flat line starting at 36 with
   * the heading beside it reading "12 of 48 points". Found on 20 August 2026.
   *
   * CARDS ARE STILL JUDGED TO EXIST AT THE END OF DAY ONE, even at mark 0. A
   * sprint is filled during its first day as often as before it, and reading
   * existence at midnight emptied the commitment to nothing — which is worse
   * than the fault being fixed. Statuses are replayed to the opening; what is
   * IN the sprint is read from the day.
   */
  const marks = [
    /*
     * THE MOMENT IT STARTED, not midnight of the day it started.
     *
     * Midnight was the first attempt and it read the commitment as nothing: a
     * sprint planned in the morning and started at seven in the evening had all
     * its cards added hours after midnight, so replaying to midnight rolled
     * every one of them back out and the chart opened at zero — then rose to
     * the full total, which reads as a fortnight of unplanned scope.
     *
     * `startedAt` is the honest instant. Falling back to midnight only where a
     * sprint has none, which is a sprint that was never formally started.
     */
    {
      date: dates[0],
      at: sprint.startedAt ?? startOf(dates[0]),
      exists: endOf(dates[0]),
    },
    ...dates.map((date) => ({ date, at: endOf(date), exists: endOf(date) })),
  ];

  let move = 0;
  const back: Day[] = [];
  for (let n = marks.length - 1; n >= 0; n--) {
    const { date, at, exists } = marks[n];
    const future = date > stop;
    while (move < moves.length && moves[move].at > at) {
      const m = moves[move++];
      const was = state.get(m.id);
      if (!was) continue;
      if (m.status !== undefined) was.status = m.status;
      if (m.sprint !== undefined) was.sprint = m.sprint;
    }
    let remaining = 0;
    let cards = 0;
    let unsized = 0;
    let finished = 0;
    if (!future) {
      for (const i of issues) {
        // A card made after this day was not in the fortnight on this day,
        // whatever replaying its moves says. Nothing records a creation, so
        // without this every card looks as old as the board.
        if ((born.get(i.id) ?? 0) > exists) continue;
        const s = state.get(i.id);
        if (!s || s.sprint !== sprint.id) continue;
        const p = points.get(i.id);
        if (isResolved(s.status)) {
          finished += p ?? 0;
          continue;
        }
        cards++;
        if (p === undefined) unsized++;
        else remaining += p;
      }
    }
    back.push({ date, remaining, cards, unsized, finished, ideal: 0, actual: !future });
  }
  const days = back.reverse();

  /*
   * THE IDEAL LINE STARTS AT WHAT WAS COMMITTED — day one's figure, the sprint
   * as it stood when it began — and reaches zero on the last day. Drawing it
   * from today's total instead would move the whole line every time work is
   * added, so a sprint could take on a fortnight of extra work and still look
   * exactly on plan.
   *
   * IT DESCENDS ON WORKING DAYS ONLY (Vinnie, 28 Oct) — flat across weekends and
   * the workspace's holidays, the standard burndown shape (Jira draws it this way),
   * so a team is not scored as behind for a day nobody was expected to work. The
   * drop at each mark is proportional to the WORKING days elapsed by then over the
   * sprint's total working days, not the raw calendar days. On the default Mon–Fri
   * calendar that is simply weekends; a close in the fortnight holds it flat too.
   * `days` runs [opening, close(dates[0]), …, close(dates[span-1])], so the opening
   * has nothing elapsed and the last close has all of it (ideal 0).
   */
  const start = days[0]?.remaining ?? 0;
  const last = days.length - 1;
  const totalWorking = dates.length ? workingDaysInclusive(dates[0], dates[dates.length - 1], cal) : 0;
  if (last === 0) {
    days[0].ideal = 0;
  } else if (totalWorking <= 0) {
    // A sprint wholly on non-working days (a close over a weekend) — keep the plain
    // calendar-day descent so a line is still drawn rather than dividing by zero.
    for (let n = 0; n <= last; n++) days[n].ideal = Math.max(0, start * (1 - n / last));
  } else {
    days[0].ideal = start; // the opening — nothing has elapsed yet
    for (let n = 1; n <= last; n++) {
      const elapsedWorking = workingDaysInclusive(dates[0], dates[n - 1], cal);
      days[n].ideal = Math.max(0, start * (1 - elapsedWorking / totalWorking));
    }
  }
  return days;
}

/** A stored moment as the day it fell on, in the reader's own timezone. */
function isoOf(at: number): string {
  return today(at);
}

/* ── What a fortnight holds ────────────────────────────────────────────────── */

export interface Velocity {
  sprintId: string;
  name: string;
  /** Points that were in it when it started. */
  committed: number;
  /** Points finished by the time it closed. */
  completed: number;
}

/**
 * WHAT PAST SPRINTS ACTUALLY GOT THROUGH — finished ones only.
 *
 * COMMITTED AND COMPLETED AS A PAIR, never completed alone. A team that finishes
 * 20 points a fortnight is either reliable or wildly over-promising, and the
 * single number cannot tell those apart. Vinnie specified this as paired bars on
 * 11 August for exactly that reason; the pair is produced here so the drawing
 * has both to draw.
 *
 * A RUNNING SPRINT IS NOT IN THIS LIST. Its completed figure is whatever has
 * been finished so far, which is always lower than what it will be — averaged
 * into a velocity it drags the number down every time, and the effect is largest
 * on the first day, when planning is most likely to be reading it.
 */
export function velocities(issues: Issue[], sprints: Sprint[], now: number = Date.now()): Velocity[] {
  return sprints
    .filter((s) => s.state === 'closed')
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn))
    .map((s) => {
      const days = burndown(issues, s, now);
      const committed = days[0]?.remaining ?? 0;
      /*
       * COUNTED, NOT SUBTRACTED. "What was there at the start less what is left
       * at the end" is the tempting one line and it is wrong the moment scope
       * moves: a sprint that committed 5 points, finished 1 and had 8 dropped on
       * it ends with 12 left, and the subtraction reports it finished nothing —
       * or worse, minus seven. The last real day's `finished` is what the team
       * actually got through.
       */
      const real = days.filter((d) => d.actual);
      const completed = real.length ? real[real.length - 1].finished : 0;
      return { sprintId: s.id, name: s.name, committed, completed };
    });
}

/**
 * THE NUMBER PLANNING NEEDS: what this team typically gets through.
 *
 * A MEDIAN, NOT A MEAN. One fortnight lost to an outage drags an average down
 * for months, and the number is being used to answer "is this too much for the
 * next two weeks" — the typical fortnight, not the arithmetic of the bad one.
 *
 * THE LAST SIX AT MOST. A team a year old has changed; a velocity averaged over
 * twenty-six sprints describes people who have since left.
 *
 * WITH AN EVEN NUMBER, THE LOWER OF THE TWO MIDDLES rather than the average of
 * them. This number is used to answer "will this fit in a fortnight", and of two
 * equally defensible answers the lower one is the one that does not encourage
 * over-filling. It also avoids quoting a velocity of 20.5, which invites the
 * question of what half a point of work is.
 *
 * `undefined` UNTIL THERE IS SOMETHING TO SAY — no closed sprint, or none that
 * finished anything. Zero would read as "this team gets nothing done", which is
 * a slander on a board that has simply not run a fortnight yet.
 */
export function typicalVelocity(history: Velocity[]): number | undefined {
  /*
   * A FORTNIGHT THAT FINISHED NOTHING IS LEFT OUT rather than counted as zero.
   * It is a sprint that went wrong — an outage, a holiday, everyone on one
   * incident — and reading it as this team's capacity halves the number they
   * plan the next one with, which is how one bad fortnight becomes two.
   */
  const recent = history.slice(-6).map((v) => v.completed).filter((n) => n > 0);
  if (!recent.length) return undefined;
  const sorted = [...recent].sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) / 2)];
}

export interface Capacity {
  points: number;
  unsized: number;
  typical?: number;
  /** How full it is against the typical fortnight, 0–1+. Absent with no history. */
  fullness?: number;
  /** What to tell the person planning, or '' when there is nothing worth saying. */
  say: string;
}

/**
 * IS THIS TOO MUCH FOR ONE FORTNIGHT?
 *
 * The capacity half of Stage 3, and the half that gets used weekly — a burndown
 * is read when a sprint is going wrong, this is read every time one is planned.
 *
 * IT ADVISES AND NEVER REFUSES. Nothing here blocks a sprint being over-filled.
 * A team that knowingly takes on a heavy fortnight because a release depends on
 * it is doing something reasonable, and a tool that argues with them is a tool
 * they turn off. It says the number and what the number resembles; the decision
 * stays theirs.
 *
 * THE UNSIZED COUNT IS SAID FIRST AND SEPARATELY when it is most of the sprint,
 * because a total of 12 points across four cards is not a small fortnight if
 * nine other cards have no size — it is an unknown fortnight wearing a small
 * number, which is worse than no number at all.
 */
export function capacityOf(issues: Issue[], typical: number | undefined): Capacity {
  const { points, unsized } = totalPoints(issues);
  const sized = issues.length - unsized;
  const out: Capacity = { points, unsized, typical, say: '' };

  if (issues.length === 0) return out;
  if (unsized > 0 && unsized >= sized) {
    out.say =
      unsized === issues.length
        ? `Nothing in here has a size, so there is no total to compare. Sizes are on the card.`
        : `${unsized} of these ${issues.length} cards have no size, so ${points} points is most of a guess.`;
    return out;
  }

  const tail = unsized > 0 ? ` (${unsized} without a size, so the real figure is higher)` : '';
  if (typical === undefined) {
    out.say = `${points} points${tail}. There is no finished sprint to compare it to yet — after one or two, this will say whether it is a normal fortnight.`;
    return out;
  }

  out.fullness = typical > 0 ? points / typical : undefined;
  const pct = Math.round((out.fullness ?? 0) * 100);
  /*
   * THE BANDS ARE WIDE ON PURPOSE. Velocity is not precise enough to justify
   * telling somebody that 104% is different from 96%, and a tool that reacts to
   * a four-point difference teaches people to game the sizes until it goes
   * quiet. Only plainly light and plainly heavy get a word.
   */
  if (pct < 60) out.say = `${points} points${tail} — a light fortnight against the usual ${typical}.`;
  else if (pct > 130)
    out.say = `${points} points${tail} — heavier than the usual ${typical}. It fits if something else gives.`;
  else out.say = `${points} points${tail} — about a normal fortnight, against the usual ${typical}.`;
  return out;
}
